import emptyCoupons from "../../assets/illustrations/empty-coupons.png";
import { useState } from "react";
import { useOpenFromQuery } from "../../hooks/useOpenFromQuery";
import { Card, Group, Text, Switch, Badge, Box, Button } from "@mantine/core";
import { IconPlus, IconEdit } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { couponsApi } from "../../api/coupons";
import {
  CouponFormModal,
  toCouponBody,
  type CouponFormValues,
} from "./CouponFormModal";
import type { Coupon } from "../../types/domain";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable, type Column } from "../../components/common/DataTable";
import { SearchInput } from "../../components/common/SearchInput";
import {
  ListDateRangeFilter,
  type DateRange,
} from "../../components/common/ListDateRangeFilter";
import { ExportMenu } from "../../components/common/ExportMenu";
import { collectAll, type ExportField } from "../../lib/export";
import {
  DetailDrawer,
  DetailField,
  DetailSection,
} from "../../components/common/DetailDrawer";
import { formatMoney, formatDate, en } from "../../lib/format";
import { formatNumber } from "../../lib/format";
import { errorMessage } from "../../lib/axios";

const couponExportFields: ExportField<Coupon>[] = [
  { label: "Code", value: (c) => c.couponCode },
  { label: "Description", value: (c) => en(c.description) },
  { label: "Discount %", value: (c) => c.discountPercentage },
  { label: "Max discount", value: (c) => c.maxDiscountAmount },
  { label: "Min spend", value: (c) => c.minApplicableAmount },
  { label: "Expires", value: (c) => c.endDate },
  { label: "Active", value: (c) => (c.isActive ? "Yes" : "No") },
];

export function CouponsPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Coupon | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  useOpenFromQuery(() => setFormOpen(true));
  const [editing, setEditing] = useState<Coupon | null>(null);
  const [range, setRange] = useState<DateRange>({});
  const filters = { startDate: range.startDate, endDate: range.endDate };
  const { page, setPage, search, onSearchChange, result, isLoading, error } =
    usePaginatedList<Coupon>({
      queryKey: ["coupons"],
      fetcher: couponsApi.list,
      filters,
    });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => couponsApi.toggleStatus(id),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Promo code updated" });
      void qc.invalidateQueries({ queryKey: ["coupons"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const saveMutation = useMutation({
    mutationFn: (values: CouponFormValues) => {
      const body = toCouponBody(values);
      return editing ? couponsApi.update(editing._id, body) : couponsApi.create(body);
    },
    onSuccess: () => {
      notifications.show({
        color: "green",
        message: editing ? "Promo code updated" : "Promo code created",
      });
      setFormOpen(false);
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ["coupons"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  // Usage stats for the open promo code.
  const usage = useQuery({
    queryKey: ["coupon-usage", selected?._id],
    queryFn: () => couponsApi.usageStats(selected!._id),
    enabled: !!selected,
  });

  const columns: Column<Coupon>[] = [
    {
      key: "code",
      header: "Code",
      render: (c) => (
        <Text ff="monospace" fw={600}>
          {c.couponCode ?? "—"}
        </Text>
      ),
    },
    {
      key: "desc",
      header: "Description",
      render: (c) => (
        <Text size="sm" lineClamp={1}>
          {en(c.description)}
        </Text>
      ),
    },
    {
      key: "discount",
      header: "Discount",
      render: (c) => (
        <Group gap={4}>
          <Badge variant="light">
            {c.discountPercentage ?? 0}%
          </Badge>
          {c.maxDiscountAmount ? (
            <Text size="xs" c="dimmed">
              max {formatMoney(c.maxDiscountAmount)}
            </Text>
          ) : null}
        </Group>
      ),
    },
    {
      key: "min",
      header: "Min spend",
      align: "right",
      render: (c) => formatMoney(c.minApplicableAmount),
    },
    {
      key: "expiry",
      header: "Expires",
      render: (c) => (
        <Text size="xs" c="dimmed">
          {formatDate(c.endDate)}
        </Text>
      ),
    },
    {
      key: "active",
      header: "Active",
      render: (c) => (
        // stop row-click from firing when toggling
        <Box onClick={(e) => e.stopPropagation()}>
          <Switch
            checked={!!c.isActive}
            onChange={() => toggleMutation.mutate(c._id)}
            size="sm"
          />
        </Box>
      ),
    },
  ];

  const stats = usage.data as Record<string, unknown> | undefined;

  return (
    <>
      <PageHeader
        title="Promo codes"
        subtitle="Promotional discount codes"
        actions={
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            New promo code
          </Button>
        }
      />
      <Card withBorder radius="md" padding="lg">
        <Group mb="md" gap="sm">
          <SearchInput
            value={search}
            onChange={onSearchChange}
            placeholder="Search promo code…"
          />
          <ListDateRangeFilter
            value={range}
            onChange={(r) => {
              setRange(r);
              setPage(1);
            }}
          />
          <ExportMenu
            filename="coupons"
            fields={couponExportFields}
            fetchAll={() =>
              collectAll(couponsApi.list, {
                search: search || undefined,
                ...filters,
              })
            }
          />
        </Group>
        <DataTable
          columns={columns}
          rows={result?.items ?? []}
          rowKey={(c) => c._id}
          loading={isLoading}
          error={error ? errorMessage(error) : null}
          emptyLabel="No promo codes yet"
          emptyImage={emptyCoupons}
          onRowClick={setSelected}
          page={page}
          totalPages={result?.totalPages}
          total={result?.total}
          onPageChange={setPage}
        />
      </Card>

      <DetailDrawer
        opened={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.couponCode ?? "Promo code"}
      >
        {selected && (
          <>
            <Button
              variant="light"
              leftSection={<IconEdit size={16} />}
              onClick={() => {
                setEditing(selected);
                setSelected(null);
                setFormOpen(true);
              }}
            >
              Edit promo code
            </Button>
            <DetailSection title="Promo code">
              <DetailField label="Code" value={selected.couponCode} />
              <DetailField label="Description" value={en(selected.description)} />
              <DetailField label="Discount" value={`${selected.discountPercentage ?? 0}%`} />
              <DetailField label="Max discount" value={formatMoney(selected.maxDiscountAmount)} />
              <DetailField label="Min spend" value={formatMoney(selected.minApplicableAmount)} />
              <DetailField label="Expires" value={formatDate(selected.endDate)} />
              <DetailField label="Active" value={selected.isActive ? "Yes" : "No"} />
            </DetailSection>
            <DetailSection title="Usage">
              {usage.isLoading ? (
                <Text size="sm" c="dimmed">Loading…</Text>
              ) : (
                <>
                  <DetailField
                    label="Times used"
                    value={formatNumber((stats?.totalUsage ?? stats?.timesUsed) as number)}
                  />
                  <DetailField
                    label="Total discount"
                    value={formatMoney((stats?.totalDiscount ?? stats?.totalDiscountAmount) as number)}
                  />
                  <DetailField
                    label="Unique users"
                    value={formatNumber(stats?.uniqueUsers as number)}
                  />
                </>
              )}
            </DetailSection>
          </>
        )}
      </DetailDrawer>

      <CouponFormModal
        opened={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        editing={editing}
        loading={saveMutation.isPending}
        onSubmit={(values) => saveMutation.mutate(values)}
      />
    </>
  );
}
